import request from "../../utils/request";
import { Result } from "../../models/general";

// CO-323 — multi-factor authentication. Mirrors Mowazaf.Server/Services/Identity/MfaPolicyService.cs
// (CompanyMfaPolicyView / CompanyMfaPolicyInput / MfaStatusView) and Controllers/General/MfaController.cs.

/** Roles an Operator may put under a company MFA policy ("Clocking" is the base employee role). */
export type MfaRole = "Operator" | "Hr" | "Admin" | "Chief" | "Manager" | "Clocking";

export interface CompanyMfaPolicy {
  companyId: number;
  /** False when the company has no policy row yet (values are the platform defaults). */
  isConfigured: boolean;
  isEnforced: boolean;
  enforcedRoles: MfaRole[];
  gracePeriodDays: number;
  rememberDeviceDays: number;
  enforcementEnabledAtUtc?: string | null;
  graceEndsAtUtc?: string | null;
  updatedOn?: string | null;
  platformEnabled: boolean;
  maxGracePeriodDays: number;
  maxRememberDeviceDays: number;
  availableRoles: MfaRole[];
}

export interface CompanyMfaPolicyInput {
  isEnforced: boolean;
  enforcedRoles: MfaRole[];
  gracePeriodDays: number;
  rememberDeviceDays: number;
}

export interface MfaStatus {
  twoFactorEnabled: boolean;
  recoveryCodesLeft: number;
  requiredByCompany: boolean;
  mustEnrolNow: boolean;
  graceEndsAtUtc?: string | null;
  /**
   * Platform master switch (`Mfa:Enabled`). When false the self-service 2FA pages only say "not available
   * yet", so the profile entry is hidden (owner decision 2026-09-15: hidden on prod for 24 h after v1.0.56).
   */
  platformEnabled: boolean;
}

/** Server-rendered page where a user manages their own two-factor settings (full page navigation). */
export const MFA_MANAGE_PATH = "/Account/Manage/TwoFactorAuthentication";

/** Operator only. */
export async function getCompanyMfaPolicy(companyId: number) {
  const response = await request.get<Result<CompanyMfaPolicy>>(`Mfa/company/${companyId}/policy`);
  return response.data;
}

/** Operator only. */
export async function updateCompanyMfaPolicy(companyId: number, input: CompanyMfaPolicyInput) {
  const response = await request.put<Result<CompanyMfaPolicy>>(`Mfa/company/${companyId}/policy`, input);
  return response.data;
}

/** Operator: any user. HR: users of their own company, never an Operator. */
export async function resetUserMfa(userId: string) {
  const response = await request.post<Result<boolean>>(`Mfa/users/${encodeURIComponent(userId)}/reset`);
  return response.data;
}

/** The signed-in user's own status. */
export async function getMyMfaStatus() {
  const response = await request.get<Result<MfaStatus>>(`Mfa/me`);
  return response.data;
}
